import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { saveWarranty, warrantyQuery, type DeviceWarrantyPage, type WarrantyForm, type WarrantyHp } from '../api/devices'
import { FormActions, FormRow, HalfField } from '../ui/FormGrid'
import { toast } from '../ui/notify'
import { DatePicker } from '../ui/DatePicker'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Warranty - inventoree/devices/others/deviceWarrantyAndFiles.php si
 * deviceWarrantyAndFiles-1.js: blocul HP cand e cazul, formularul cu Brand /
 * Serial / inventory / location, cele trei documente de 4 MB, datele cu
 * datetimepicker pe zi, Update cu confirm si toastr Data saved!.
 *
 * ASEZAREA e cea comuna (`ui/FormGrid.tsx`): eticheta deasupra campului, doua
 * coloane de aceeasi latime. Inainte, cele patru campuri de sus stateau pe
 * `form-horizontal` cu eticheta la stanga, iar cele trei perechi document/data
 * pe o A DOUA impartire, cu eticheta pe jumatate din jumatate de panou -
 * masurat la 1600px, casuta de fisier incepea la 428px si data la 1143px, adica
 * doua margini de coloana diferite in acelasi formular. Toate etichetele
 * stateau intr-un `<span class="color">`, pe care `_layout.scss` il deseneaza
 * ca pe o pastila gri de 25px; `od_console.css` o stinge, dar numai in zonele
 * care poarta `od-console`, si fisa aparatului nu e una dintre ele.
 *
 * CASUTA DE FISIER RAMANE FARA `form-control`: cu inaltimea fixa a clasei isi
 * taie singura butonul nativ. Sta pe coloana, cu eticheta deasupra ca toate
 * celelalte, dar desenata de browser - vezi si nota din capul lui `FormGrid`.
 *
 * Blocul HP de deasupra ramane cum era: e o CITIRE, cu eticheta langa valoare.
 * Sapte randuri puse pe cate doua etaje ar face un panou de doua ori mai inalt
 * fara sa spuna nimic in plus.
 *
 * CE NU S-A SCHIMBAT: campurile, ordinea, numele lor si ce se trimite.
 */

export function DeviceWarranty({ id }: ScreenProps) {
  const { data, error } = useQuery(warrantyQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Warranty page={data} />
}

function Warranty({ page }: { page: DeviceWarrantyPage }) {
  const query = useQueryClient()
  const [form, setForm] = useState(page.form)
  const [files, setFiles] = useState<Record<string, File | null>>({
    warranty_doc: null,
    extra_warranty_doc: null,
    warranty_bill_doc: null,
  })

  useEffect(() => {
    setForm(page.form)
  }, [page])

  const set = (key: keyof WarrantyForm, value: string) => setForm((prev) => ({ ...prev, [key]: value }))

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (!confirm('Are you sure?')) return
    const body = new FormData()
    body.set('device', String(page.device.id))
    body.set('device_brand', form.brandId)
    body.set('device_serial_number', form.serial)
    body.set('device_inventory_number', form.inventory)
    body.set('device_location', form.location)
    body.set('device_warranty_expiry_date', form.warrantyExpiry)
    body.set('device_extended_warranty_expiry_date', form.extendedExpiry)
    body.set('device_acquisition_date', form.acquisition)
    for (const [key, file] of Object.entries(files)) {
      if (file) body.set(key, file)
    }
    try {
      await saveWarranty(page.device.id, body)
      toast('Data saved!', 'Success', 'success')
      await query.invalidateQueries({ queryKey: ['device', page.device.id, 'warranty'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Error', 'Error', 'error')
    }
  }

  return (
    <div className="device-warranty">
      {page.hp && <HpBlock hp={page.hp} />}

      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Warranty</strong> and <strong>File</strong> upload
        </h4>
      </header>
      <div className="panel-body">
        <form className="updateDeviceDetails" method="post" onSubmit={submit}>
          <input type="hidden" name="device" value={page.device.id} />

          <FormRow>
            <HalfField label="Brand" htmlFor="dw_device_brand">
              <select
                id="dw_device_brand"
                className="form-control"
                name="device_brand"
                value={form.brandId}
                onChange={(e) => set('brandId', e.target.value)}
              >
                <option>Select...</option>
                {page.brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </HalfField>
            <HalfField label="Serial number" htmlFor="dw_device_serial_number">
              <input
                id="dw_device_serial_number"
                className="form-control"
                type="text"
                name="device_serial_number"
                value={form.serial}
                onChange={(e) => set('serial', e.target.value)}
              />
            </HalfField>
          </FormRow>

          <FormRow>
            <HalfField label="Previous inventory number" htmlFor="dw_device_inventory_number">
              <input
                id="dw_device_inventory_number"
                className="form-control"
                type="text"
                name="device_inventory_number"
                value={form.inventory}
                onChange={(e) => set('inventory', e.target.value)}
              />
            </HalfField>
            <HalfField label="Device location" htmlFor="dw_device_location">
              <input
                id="dw_device_location"
                className="form-control"
                type="text"
                name="device_location"
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
              />
            </HalfField>
          </FormRow>

          <FileDate
            fileLabel="Warranty document"
            fileName="warranty_doc"
            dateLabel="Warranty expiry date"
            dateName="device_warranty_expiry_date"
            date={form.warrantyExpiry}
            onDate={(v) => set('warrantyExpiry', v)}
            onFile={(f) => setFiles((p) => ({ ...p, warranty_doc: f }))}
          />
          <FileDate
            fileLabel="Extended Warranty document"
            fileName="extra_warranty_doc"
            dateLabel="Extended warranty expiry date"
            dateName="device_extended_warranty_expiry_date"
            date={form.extendedExpiry}
            onDate={(v) => set('extendedExpiry', v)}
            onFile={(f) => setFiles((p) => ({ ...p, extra_warranty_doc: f }))}
          />
          <FileDate
            fileLabel="Aquisition document"
            fileName="warranty_bill_doc"
            dateLabel="Acquisition date"
            dateName="device_acquisition_date"
            date={form.acquisition}
            onDate={(v) => set('acquisition', v)}
            onFile={(f) => setFiles((p) => ({ ...p, warranty_bill_doc: f }))}
            dateIcon="far fa-calendar-check ico"
          />

          <FormActions>
            <button type="submit" className="btn btn-theme">
              Update
            </button>
          </FormActions>
        </form>
      </div>
    </div>
  )
}

function HpBlock({ hp }: { hp: WarrantyHp }) {
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Warranty</strong> results
        </h4>
      </header>
      <div className="panel-body">
        <div className="col-md-12">
          <Pair label="Serial number" value={hp.sn} />
          <Pair label="Service type" value={hp.serviceType} />
          <Pair label="Type" value={hp.type} />
          <label className="col-md-3">Status</label>
          <label className={`col-md-9 ${hp.statusTone}`} style={{ fontWeight: 'normal' }}>
            {hp.status}
          </label>
          <Pair label="Start date" value={hp.startDate} />
          <Pair label="End date" value={hp.endDate} />
          <Pair label="Service level" value={hp.serviceLevel} />
        </div>
      </div>
      <div className="alert alert-warning">
        <i className="fas fa-exclamation-triangle"></i> Please note that these data come from the beta version of the API
        from HP. You can validate the warranty by clicking{' '}
        <a href="https://support.hp.com/us-en/checkwarranty" target="_blank" rel="noopener noreferrer" style={{ color: '#337ab7' }}>
          here
        </a>
        , or you can complete the data below.
      </div>
    </>
  )
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <>
      <label className="col-md-3">{label}</label>
      <label className="col-md-9" style={{ fontWeight: 'normal' }}>
        {value}
      </label>
    </>
  )
}

function FileDate({
  fileLabel,
  fileName,
  dateLabel,
  dateName,
  date,
  onDate,
  onFile,
  dateIcon,
}: {
  fileLabel: string
  fileName: string
  dateLabel: string
  dateName: string
  date: string
  onDate(next: string): void
  onFile(file: File | null): void
  dateIcon?: string
}) {
  return (
    <FormRow>
      <HalfField label={fileLabel} htmlFor={fileName} help="max file size 4MB.">
        <input type="file" id={fileName} name={fileName} onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
      </HalfField>
      <HalfField label={dateLabel}>
        <DatePicker value={date} name={dateName} onChange={onDate} icon={dateIcon} />
      </HalfField>
    </FormRow>
  )
}
